<template>
  <DxPopup
    v-if="visible"
    :visible="visible"
    width="100vw"
    height="100vh"
    :show-close-button="true"
    :hide-on-outside-click="false"
    :wrapper-attr="{ class: 'lot-selector-popup' }"
    @hiding="handleHiding"
  >
    <template #title>
      <div>
        <div class="lot-selector-header">
          <div class="lot-selector-title">{{ transactionType || "" }} \ {{ productName || "-" }}  \ {{ targetQuantity }} {{ measureUnitSymbol || "" }}</div>
          <div class="lot-selector-total"></div>
        </div>
        <div class="lot-selector-header">
          <div class="lot-selector-title">{{ /*$t("lot_items.available_title")*/ }}</div>
          <div class="lot-selector-total">{{ totalAvailable }} {{ measureUnitSymbol || "" }} / {{ selectedTotal }} {{ measureUnitSymbol || "" }}</div>
        </div>
      </div>
    </template>
    <template #content>
      <div class="lot-selector-content">
        <div v-if="loading" class="lot-selector-loading">{{ $t("lot_items.loading") }}</div>
        <div v-else-if="!items.length" class="lot-selector-empty">
          {{ $t("lot_items.available_empty") }}
        </div>
        <div v-else class="lot-selector-grid">
          <DxDataGrid
            ref="lotGrid"
            class="grid_general_mobile_style"
            :data-source="items"
            key-expr="group_key"
            :remote-operations="false"
            :hover-state-enabled="true"
            :repaint-changes-only="true"
            :selected-row-keys="selectedRowKeys"
            :element-attr="gridAttributes"
            :no-data-text="$t('lot_items.available_empty')"
            height="100%"
            @row-click="onRowClick"
            @row-updated="onRowUpdated"
            @cell-prepared="onCellPrepared"
          >
            <DxSelection mode="multiple" showCheckBoxesMode="none" :select-by-click="true" />
            <DxFilterRow :visible="true" />
            <DxPaging :enabled="true" :page-size="10" />
            <DxScrolling mode="virtual" />
            <DxSorting mode="multiple" />
            <DxEditing mode="cell" :allow-updating="true" />

            <DxColumn
              data-field="lot_search"
              :caption="$t('lot_items.lot')"
              cell-template="lotCell"
              :allow-sorting="false"
              :allow-editing="false"
            />
            <DxColumn
              data-field="expiration_date_display"
              :caption="$t('common.expiration_date')"
              cell-template="expirationCell"
              :calculate-sort-value="getExpirationSortValue"
              sort-order="asc"
              :sort-index="0"
              :allow-editing="false"
              width="120px"
            />
            <DxColumn
              data-field="_selected_quantity"
              :caption="$t('common.quantity') + ' (' + (measureUnitSymbol || '') + ')'"
              alignment="right"
              :dataType="'number'"
              :allow-filtering="false"
              :allow-sorting="false"
            />

            <template #lotCell="{ data }">
              <div class="lot-selector-lot-cell">
                <div class="lot-selector-lot-main">{{ data.data.lot || "-" }}</div>
                <div class="lot-selector-lot-sub">{{ formatQty(data.data.available_quantity) }} {{ measureUnitSymbol || "" }}</div>
              </div>
            </template>
            <template #expirationCell="{ data }">
              <span>{{ data.data.expiration_date_display || "-" }}</span>
            </template>
          </DxDataGrid>
        </div>
        <div v-if="errorMessage" class="lot-selector-error">
          {{ errorMessage }}
        </div>
        <div class="lot-selector-actions">
          <button type="button" class="lot-selector-cancel" @click="discard">
            {{ $t("lot_items.discard") }}
          </button>
          <button type="button" class="lot-selector-save" @click="save">
            {{ $t("lot_items.save") }}
          </button>
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { DxPopup } from "devextreme-vue/popup";
import DxNumberBox from "devextreme-vue/number-box";
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxPaging, DxScrolling, DxSorting, DxEditing } from "devextreme-vue/data-grid";
import { getQuantityFormatter, mapAvailableLotGroups, splitQuantityAcrossLotRows } from "~/helpers/stockHelper";
import moment from "moment";

export default {
  name: "LotItemsSelectorPopup",
  components: {
    DxPopup,
    DxNumberBox,
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxFilterRow,
    DxPaging,
    DxScrolling,
    DxSorting,
    DxEditing,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    productUuid: {
      type: String,
      default: "",
    },
    warehouseUuid: {
      type: String,
      default: "",
    },
    movementDate: {
      type: String,
      default: "",
    },
    targetQuantity: {
      type: Number,
      default: 0,
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
    productName: {
      type: String,
      default: "",
    },
    transactionType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      showError: false,
      selectedRowKeys: [],
      gridAttributes: {
        class: "smagria-grid lot-selector-grid",
      },
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    quantityFormat() {
      return getQuantityFormatter() + " " + (this.measureUnitSymbol || "");
    },
    selectedTotal() {
      const total = this.items.reduce((sum, item) => {
        if (!item._selected) {
          return sum;
        }
        return sum + (parseFloat(item._selected_quantity) || 0);
      }, 0);
      return parseFloat(total.toFixed(4));
    },
    totalAvailable() {
      const total = this.items.reduce((sum, item) => {
        return sum + (parseFloat(item.available_quantity) || 0);
      }, 0);
      return parseFloat(total.toFixed(4));
    },
    errorMessage() {
      if (!this.showError) {
        return "";
      }
      if (!this.items.some((item) => item._selected)) {
        return this.$t("lot_items.select_required");
      }
      if (this.items.some((item) => item._selected && !this.isQtyValid(item))) {
        return this.$t("lot_items.invalid_quantity");
      }
      if (!this.isSumValid) {
        return this.$t("lot_items.sum_mismatch");
      }
      return "";
    },
    isSumValid() {
      const qty = parseFloat(this.targetQuantity) || 0;
      if (qty <= 0) {
        return false;
      }
      return Math.abs(this.selectedTotal - qty) <= 0.0001;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.showError = false;
        this.fetchAvailable();
      }
    },
  },
  methods: {
    ...mapActions({
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
    }),
    async fetchAvailable() {
      if (!this.productUuid || !this.warehouseUuid || !this.movementDate) {
        this.items = [];
        this.selectedRowKeys = [];
        return;
      }
      this.loading = true;
      try {
        const payload = {
          product_uuid: this.productUuid,
          warehouse_uuid: this.warehouseUuid,
          movement_date: this.movementDate,
          enablePagination: 0,
          limit: 99999,
        };
        const response = await this.getSmStockMovementItemsAvailableSum(payload);
        const data = response?.data?.data || response?.data || [];
        this.items = mapAvailableLotGroups(data).map((group) => ({
          ...group,
          _selected: false,
          _selected_quantity: 0,
          expiration_date_raw: group.expiration_date || null,
          expiration_date_display: this.formatExpirationDate(group.expiration_date),
          lot_search: `${group.lot || ""} ${this.formatQty(group.available_quantity)} ${this.measureUnitSymbol || ""}`.trim(),
        }));
        this.selectedRowKeys = [];
      } finally {
        this.loading = false;
      }
    },
    formatQty(value) {
      const num = typeof value === "number" ? value : parseFloat(value);
      return Number.isFinite(num) ? parseFloat(num.toFixed(4)) : 0;
    },
    formatExpirationDate(value) {
      if (!value) {
        return "-";
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return date.format(this.getDefaultDateFormat);
    },
    getExpirationSortValue(rowData) {
      return rowData?.expiration_date_raw || rowData?.expiration_date || null;
    },
    isQtyValid(item) {
      const qty = parseFloat(item._selected_quantity) || 0;
      const max = parseFloat(item.available_quantity) || 0;
      return qty > 0 && qty <= max;
    },
    onRowClick(e) {
      if (e.rowType !== "data") {
        return;
      }
      if (this.isRowClickFromEditor(e)) {
        return;
      }
      const rowKey = e.key;
      const rowIndex = this.items.findIndex((item) => item.group_key === rowKey);
      if (rowIndex === -1) {
        return;
      }

      const selectedIndex = this.selectedRowKeys.indexOf(rowKey);
      if (selectedIndex === -1) {
        this.selectedRowKeys.push(rowKey);
        const row = this.items[rowIndex];
        //const nextQty = row._selected_quantity > 0 ? row._selected_quantity : row.available_quantity;
        this.$set(this.items, rowIndex, {
          ...row,
          _selected: true,
          _selected_quantity: 0,
        });
      } else {
        this.selectedRowKeys.splice(selectedIndex, 1);
        const row = this.items[rowIndex];
        this.$set(this.items, rowIndex, {
          ...row,
          _selected: false,
          _selected_quantity: 0,
        });
      }

      this.$refs.lotGrid.instance.selectRows(this.selectedRowKeys, false);
      this.showError = false;
    },
    onRowUpdated(e) {
      const rowKey = e.key;
      const rowIndex = this.items.findIndex((item) => item.group_key === rowKey);
      if (rowIndex === -1) {
        return;
      }
      const row = this.items[rowIndex];
      const max = parseFloat(row.available_quantity) || 0;
      let qty = parseFloat(row._selected_quantity) || 0;

      if (qty > max) {
        qty = max;
      }
      if (qty < 0) {
        qty = 0;
      }

      const isSelected = qty > 0;
      this.$set(this.items, rowIndex, {
        ...row,
        _selected: isSelected,
        _selected_quantity: qty,
      });

      if (isSelected && !this.selectedRowKeys.includes(rowKey)) {
        this.selectedRowKeys.push(rowKey);
      } else if (!isSelected && this.selectedRowKeys.includes(rowKey)) {
        this.selectedRowKeys = this.selectedRowKeys.filter((key) => key !== rowKey);
      }

      this.$refs.lotGrid.instance.selectRows(this.selectedRowKeys, false);
      this.showError = false;
    },
    isRowClickFromEditor(e) {
      const target = e?.event?.target;
      if (!target || !target.closest) {
        return false;
      }
      return !!target.closest(".dx-texteditor, .dx-editor-cell, .dx-numberbox, .dx-dropdowneditor, input, textarea, button");
    },
    handleHiding(e) {
      if (!this.isValidSelection()) {
        if (e && Object.prototype.hasOwnProperty.call(e, "cancel")) {
          e.cancel = true;
        }
        this.showError = true;
      }
    },
    onCellPrepared(e) {
      // Only for data rows and editable columns
      if (e.rowType === "data" && e.column.allowEditing) {
        e.cellElement.classList.add("editable-cell-yellow");
      }
    },
    isValidSelection() {
      if (!this.items.some((item) => item._selected)) {
        return false;
      }
      if (this.items.some((item) => item._selected && !this.isQtyValid(item))) {
        return false;
      }
      return this.isSumValid;
    },
    discard() {
      this.$emit("update:visible", false);
    },
    save() {
      this.showError = true;
      if (!this.isValidSelection()) {
        return;
      }
      // Split each selected lot group quantity across its underlying stock
      // movement item rows (FIFO), so the payload stays row-level as before.
      const selected = this.items
        .filter((item) => item._selected)
        .flatMap((item) => splitQuantityAcrossLotRows(item, parseFloat(item._selected_quantity) || 0));
      this.$emit("save", selected);
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped>
:global(.lot-selector-popup .dx-popup-content) {
  padding: 0 !important;
}

.lot-selector-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.lot-selector-title {
  font-weight: 700;
  font-size: 15px;
}

.lot-selector-total {
  font-size: 12px;
  font-weight: 600;
  color: #3c3c3c;
}

.lot-selector-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
}

.lot-selector-loading,
.lot-selector-empty {
  font-size: 12px;
  color: #797979;
  margin-bottom: 8px;
}

.lot-selector-lot-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lot-selector-lot-main {
  font-weight: 600;
  font-size: 13px;
}

.lot-selector-lot-sub {
  font-size: 12px;
  color: #6b6b6b;
}

.lot-selector-grid {
  flex: 1 1 auto;
  min-height: 220px;
}

::v-deep .lot-selector-grid .dx-datagrid-rowsview .dx-row > td {
  vertical-align: middle;
}

.lot-selector-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 16px 0 24px;
}

.lot-selector-cancel {
  background: #ffffff;
  border: 1px solid #d1d1d1;
  color: #3a3a3a;
  padding: 10px 12px;
  border-radius: 56px;
  font-weight: 600;
}

.lot-selector-save {
  background: #d06110;
  border: none;
  color: #ffffff;
  padding: 10px 12px;
  border-radius: 56px;
  font-weight: 600;
}

.lot-selector-error {
  margin-top: 6px;
  color: #dc3545;
  font-size: 12px;
}
</style>
